import React, { PropsWithChildren, useMemo } from 'react';

import { useForm } from 'react-hook-form';

import { useTranslate } from '@i18n';
import { useCategories, useUiNodes, useUiGraph } from '@queries';
import { Button, Divider, Input, ModalBase, ModalBody, ModalFooter, ModalHeader, Text, u } from '@ui';

import { Pill, PillGroup } from '../../PillGroup/PillGroup';

import { ModuleSelectionItem } from './ModuleSelectionItem';
import * as css from './ModuleSelectionModal.css';

type LayoutPresetModalProps = {
    onSelect?: (moduleId: string, name: string) => void;
    onDismiss: () => void;
    selectedIds: string[];
};

export const useModuleSelectionForm = (selectedIds?: string[]) => {
    const { register, watch, setValue } = useForm<{ filter: string; categories: string[] }>({
        defaultValues: { filter: '', categories: [] },
    });

    const selectedCategories = watch('categories');
    const filter = watch('filter');

    const { data: categories } = useCategories('graph_type');

    const categoriesMap = useMemo(
        () => Object.fromEntries(categories?.map((category) => [category.id, category] as const) || []),
        [categories],
    );
    const { data: graphs = [] } = useUiGraph();

    const { data: modules = [] } = useUiNodes('module');

    const t = useTranslate();

    const filteredGraphs = useMemo(
        () =>
            graphs.filter(
                ({ id, i18n, categories }) =>
                    t(i18n ?? '')
                        .toLowerCase()
                        .includes(filter.toLowerCase()) &&
                    (!selectedCategories.length || selectedCategories.includes(categories.graphType)) &&
                    !selectedIds?.includes(id),
            ),
        [filter, graphs, selectedCategories, selectedIds, t],
    );

    const newCustomModule = useMemo(
        () => modules.find((module) => !selectedIds?.includes(module.id)),
        [modules, selectedIds],
    );

    const orderedGraphs = useMemo(
        () =>
            filteredGraphs.sort(
                (a, b) =>
                    categoriesMap[a.categories.graphType]?.displayOrder -
                    categoriesMap[b.categories.graphType]?.displayOrder,
            ),
        [categoriesMap, filteredGraphs],
    );

    const tableIsSelected = useMemo(() => selectedIds?.includes('module.table'), [selectedIds]);

    const shouldShowTableModule =
        selectedCategories.length === 0 && !tableIsSelected && 'table'.includes(filter.toLowerCase());

    return {
        register,
        orderedGraphs,
        setValue,
        categories,
        selectedCategories,
        categoriesMap,
        shouldShowTableModule,
        newCustomModule,
    };
};

export const ModuleList = ({ children }: PropsWithChildren) => (
    <div className={css.inputGroup}>
        {!React.Children.count(children) && (
            <Text variant="p" color="muted">
                No results found
            </Text>
        )}
        {children}
    </div>
);

export const ModuleSelectionModal = ({ onDismiss, onSelect, selectedIds }: LayoutPresetModalProps) => {
    const {
        register,
        setValue,
        orderedGraphs,
        categories,
        selectedCategories,
        categoriesMap,
        shouldShowTableModule,
        newCustomModule,
    } = useModuleSelectionForm(selectedIds);

    const t = useTranslate();

    return (
        <ModalBase onDismiss={onDismiss} open variant="partial">
            <ModalHeader>Module selection</ModalHeader>
            <ModalBody pb={u(4)}>
                <PillGroup search={<Input {...register('filter')} placeholder="Search (layout module)" />}>
                    {categories?.map(({ id, i18n }) => {
                        const isSelected = selectedCategories.includes(id);
                        return (
                            <Pill
                                key={id}
                                isSelected={isSelected}
                                onClick={() => {
                                    setValue(
                                        'categories',
                                        isSelected
                                            ? selectedCategories.filter((sid) => sid !== id)
                                            : [...selectedCategories, id],
                                        { shouldValidate: true },
                                    );
                                }}
                                label={t(i18n ?? '-')}
                            />
                        );
                    })}
                </PillGroup>
            </ModalBody>
            <Divider />
            <ModalBody py={u(4)} flexShrink={1}>
                <ModuleList>
                    {shouldShowTableModule ? (
                        <ModuleSelectionItem
                            label={'Table Module'}
                            category="Custom"
                            onClick={() => onSelect?.('module.table', 'Table Module')}
                        />
                    ) : null}
                    {newCustomModule && (
                        <ModuleSelectionItem
                            label={'New Custom Module'}
                            category="Custom"
                            onClick={() => onSelect?.(newCustomModule?.id, 'Custom Module')}
                        />
                    )}
                    <Divider />
                    {orderedGraphs.map(({ id, i18n, categories }) => (
                        <ModuleSelectionItem
                            key={id}
                            label={t(i18n ?? '-')}
                            category={t(categoriesMap?.[categories.graphType]?.i18n ?? '-')}
                            onClick={() => onSelect?.(id, t(i18n ?? '-'))}
                        />
                    ))}
                </ModuleList>
            </ModalBody>

            <ModalFooter
                primary={
                    <Button variant="secondary" size="medium" onClick={onDismiss}>
                        {t('generic_ui.close')}
                    </Button>
                }
            />
        </ModalBase>
    );
};
